<template>
  <v-dialog
    v-model="isOpen"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    max-width="460px"
    persistent>
    <v-card v-if="game">
      <v-card-title class="text-h6 d-flex align-center">
        <span>{{ $t('sharedAccounts.banDialog.title') }}</span>
        <v-spacer />
        <v-btn icon :disabled="loading" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div class="mb-3">
          <div class="caption text--secondary">
            {{ $t('sharedAccounts.attributes.username') }}
          </div>
          <div class="subtitle-1 font-weight-medium">
            {{ account ? account.username : '' }}
          </div>
        </div>
        <div class="mb-4">
          <div class="caption text--secondary">
            {{ $t('sharedAccounts.attributes.game') }}
          </div>
          <div class="subtitle-1 font-weight-medium">{{ game.name }}</div>
        </div>

        <v-alert v-if="currentlyBanned" dense text type="error" class="mb-4">
          {{ $t('sharedAccounts.bannedUntil') }}
          {{ formatDate(new Date(game.banned_until), false) }}
        </v-alert>
        <v-alert v-else dense text type="success" class="mb-4">
          {{ $t('sharedAccounts.available') }}
        </v-alert>

        <v-alert
          dense
          text
          type="info"
          icon="mdi-information-outline"
          class="mb-4 caption">
          {{ $t('sharedAccounts.banDialog.globalScope') }}
        </v-alert>

        <div class="subtitle-2 mb-2">
          {{ $t('sharedAccounts.banDialog.setBanUntil') }}
        </div>
        <v-row dense>
          <v-col cols="7">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="date"
                  :label="$t('sharedAccounts.banDialog.date')"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  clearable
                  hide-details="auto"
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on" />
              </template>
              <v-date-picker
                v-model="date"
                :locale="$i18n.locale"
                :min="minDate"
                @input="dateMenu = false" />
            </v-menu>
          </v-col>
          <v-col cols="5">
            <v-menu
              v-model="timeMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="time"
                  :label="$t('sharedAccounts.banDialog.time')"
                  prepend-inner-icon="mdi-clock-outline"
                  :disabled="!date"
                  readonly
                  hide-details="auto"
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on" />
              </template>
              <v-time-picker
                v-model="time"
                format="24hr"
                @click:minute="timeMenu = false" />
            </v-menu>
          </v-col>
        </v-row>
        <p v-if="invalidDate" class="error--text caption mt-2 mb-0">
          {{ $t('sharedAccounts.banDialog.mustBeFuture') }}
        </p>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-btn
          v-if="currentlyBanned"
          color="error"
          outlined
          :loading="loading"
          :disabled="loading"
          @click="removeBan">
          {{ $t('sharedAccounts.banDialog.removeBan') }}
        </v-btn>
        <v-spacer />
        <v-btn text :disabled="loading" @click="close">
          {{ $t('miscellaneous.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="loading || !date || invalidDate"
          @click="save">
          {{ $t('miscellaneous.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import formatDate from '@/mixins/format-date'
import { isGameBanned } from '@/utils/shared-accounts'

export default {
  name: 'BanGameDialog',
  mixins: [formatDate],
  props: {
    value: { type: Boolean, default: false },
    account: { type: Object, default: null },
    game: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      date: null,
      time: '00:00',
      dateMenu: false,
      timeMenu: false,
    }
  },
  computed: {
    isOpen: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    currentlyBanned() {
      return this.game ? isGameBanned(this.game.banned_until) : false
    },
    minDate() {
      // Local "today" so the picker's min matches the operator's timezone.
      return this.toLocalDateString(new Date())
    },
    selectedDateTime() {
      if (!this.date) return null
      return new Date(`${this.date}T${this.time || '00:00'}`)
    },
    invalidDate() {
      const dt = this.selectedDateTime
      if (!dt) return false
      return dt.getTime() <= Date.now()
    },
  },
  watch: {
    value(open) {
      if (open) this.prefill()
    },
  },
  methods: {
    prefill() {
      this.dateMenu = false
      this.timeMenu = false
      if (this.game && this.game.banned_until) {
        const d = new Date(this.game.banned_until)
        if (!isNaN(d.getTime())) {
          this.date = this.toLocalDateString(d)
          this.time = this.toLocalTimeString(d)
          return
        }
      }
      this.date = null
      this.time = '00:00'
    },
    toLocalDateString(d) {
      const y = d.getFullYear()
      const m = ('0' + (d.getMonth() + 1)).slice(-2)
      const day = ('0' + d.getDate()).slice(-2)
      return `${y}-${m}-${day}`
    },
    toLocalTimeString(d) {
      const h = ('0' + d.getHours()).slice(-2)
      const min = ('0' + d.getMinutes()).slice(-2)
      return `${h}:${min}`
    },
    save() {
      if (!this.date || this.invalidDate) return
      // Local date/time -> UTC ISO string for the backend
      this.$emit('save', this.selectedDateTime.toISOString())
    },
    removeBan() {
      this.$emit('save', null)
    },
    close() {
      this.isOpen = false
    },
  },
}
</script>
